'use client';

import {
  AlertTriangle,
  CheckCircle2,
  ChefHat,
  ChevronRight,
  FileText,
  Loader2,
  Play,
  Printer,
  ReceiptText,
  RefreshCw,
  Search,
  Wifi,
  WifiOff,
} from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import type { PrintChannel } from '@/features/printing/domain/service/print-channels';
import { usePrintingActions, usePrintingState } from '@/features/printing/presentation/providers/printing-provider';
import { createMockOrder } from '@/features/printing/presentation/utils/mock-print-order';
import { fetchPrintService } from '@/features/printing/presentation/utils/print-tickets';
import { cn } from '@/shared/utils/utils';

interface Props {
  locationSlug?: string;
}

const NONE_VALUE = '__none__';

const CHANNEL_ROWS: {
  channel: PrintChannel;
  label: string;
  hint: string;
  icon: typeof ReceiptText;
}[] = [
  { channel: 'caja', label: 'Ticket de orden', hint: 'Se imprime al cobrar', icon: ReceiptText },
  { channel: 'cocina', label: 'Comanda de cocina', hint: 'Se envía a preparación', icon: ChefHat },
  { channel: 'fiscal', label: 'Factura AFIP', hint: 'Comprobante fiscal', icon: FileText },
];

function ReachabilityBadge({ value }: { value: 'checking' | 'reachable' | 'unreachable' | 'unknown' | undefined }) {
  if (value === 'checking') {
    return (
      <Badge variant="outline" className="gap-1 border-border text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        Verificando
      </Badge>
    );
  }

  if (value === 'reachable') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-emerald-500/40 bg-emerald-500/10 text-xs text-emerald-600 dark:text-emerald-400"
      >
        <Wifi className="size-3" />
        En tu red
      </Badge>
    );
  }

  if (value === 'unreachable') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-rose-500/40 bg-rose-500/10 text-xs text-rose-600 dark:text-rose-400"
      >
        <WifiOff className="size-3" />
        Sin respuesta
      </Badge>
    );
  }

  return null;
}

export function PrintersStatusBadge({ locationSlug }: Props) {
  const [open, setOpen] = useState(false);
  const [testingChannel, setTestingChannel] = useState<PrintChannel | null>(null);
  const [customIp, setCustomIp] = useState('192.168.68.166');
  const [customPort, setCustomPort] = useState('9100');

  const {
    printers,
    assignments,
    channelTargets,
    reachability,
    isProbing,
    probeState,
    hasReachablePrinter,
    serviceStatus,
    isLoadingPrinters,
  } = usePrintingState();
  const { setChannelPrinter, saveCustomPrinter, probeNetwork, refreshService } = usePrintingActions();

  const isLocalConnected = serviceStatus?.isHealthy ?? false;
  const cajaReachability = channelTargets.caja ? reachability[channelTargets.caja.ip] : undefined;

  const showNoPrinterInNetworkWarning = probeState === 'done' && !hasReachablePrinter;

  const handleTestPrint = async (channel: PrintChannel) => {
    const target = channelTargets[channel];

    if (!target) {
      toast.error('No hay impresora asignada para este canal');
      return;
    }

    setTestingChannel(channel);

    try {
      const mockOrder = createMockOrder(channel === 'cocina' ? 'TEST-COCINA' : 'TEST-CAJA');

      const promise = fetchPrintService('/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          printer: { ip: target.ip, port: target.port },
          type: channel === 'cocina' ? 'kitchen' : 'totem',
          order: mockOrder,
        }),
      }).then(async response => {
        if (!response.ok) {
          const errorText = await response.text().catch(() => '');
          throw new Error(`El driver respondió ${response.status} - ${errorText}`);
        }
        return response;
      });

      await toast.promise(promise, {
        loading: `Enviando prueba a ${target.name} (${target.ip}:${target.port})...`,
        success: `¡Ticket de prueba enviado a ${target.name}!`,
        error: error => `Error: ${error instanceof Error ? error.message : String(error)}`,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo conectar con el servicio local de impresión');
    } finally {
      setTestingChannel(null);
    }
  };

  const badgeColorClass = !isLocalConnected
    ? 'border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400 hover:bg-rose-500/20'
    : channelTargets.caja && cajaReachability !== 'unreachable'
      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20'
      : 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20';

  const printerLabel = isLoadingPrinters ? 'Cargando...' : (channelTargets.caja?.name ?? 'Configurar impresoras');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex h-7 cursor-pointer items-center gap-1.5 rounded-full border px-2.5 text-xs font-semibold transition-all',
          badgeColorClass
        )}
        title="Impresoras por canal (orden, cocina, AFIP)"
      >
        <span
          className={cn(
            'size-2 rounded-full',
            !isLocalConnected
              ? 'bg-rose-500'
              : channelTargets.caja && cajaReachability !== 'unreachable'
                ? 'animate-pulse bg-emerald-500'
                : 'bg-amber-500'
          )}
        />
        <Printer className="size-3.5" />
        <span className="max-w-32.5 truncate">{printerLabel}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Printer className="size-5 text-primary" />
              Impresoras de esta caja
            </DialogTitle>
            <DialogDescription>
              Asigná una impresora a cada canal. Se guarda en esta computadora y las impresoras deben estar en la misma
              red.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* 1. Estado del driver local */}
            <div
              className={cn(
                'flex items-center justify-between rounded-xl border p-3.5 text-xs',
                isLocalConnected
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200'
                  : 'border-rose-500/30 bg-rose-500/10 text-rose-900 dark:text-rose-200'
              )}
            >
              <div className="flex items-center gap-2.5">
                {isLocalConnected ? (
                  <CheckCircle2 className="size-5 shrink-0 text-emerald-500" />
                ) : (
                  <AlertTriangle className="size-5 shrink-0 text-rose-500" />
                )}
                <div>
                  <p className="text-sm font-semibold">
                    {isLocalConnected ? 'Servicio Local Conectado' : 'Servicio Local No Detectado'}
                  </p>
                  <p className="text-xs opacity-80">
                    {isLocalConnected
                      ? `Agente activo (v${serviceStatus?.version || 'desconocida'})`
                      : 'Asegurate de que printer-service esté en ejecución en esta computadora'}
                  </p>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 gap-1.5 bg-background/60 px-3 text-xs"
                onClick={refreshService}
              >
                <RefreshCw className="size-3" />
                Reintentar
              </Button>
            </div>

            {/* 2. Aviso de red: ninguna impresora responde */}
            {showNoPrinterInNetworkWarning && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5 text-xs text-amber-900 dark:text-amber-200">
                <div className="flex items-start gap-2.5">
                  <WifiOff className="mt-0.5 size-5 shrink-0 text-amber-500" />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">No detectamos impresoras en tu red WiFi</p>
                    <p className="opacity-90">
                      Ninguna de las impresoras cargadas respondió. Verificá que estén encendidas y conectadas a la
                      misma red que esta computadora, o cargá una IP manual abajo.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 3. Asignación por canal */}
            <div className="space-y-3 rounded-xl border bg-muted/20 p-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
                  Asignación por canal
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 px-2 text-xs"
                  onClick={() => void probeNetwork()}
                  disabled={isProbing || printers.length === 0}
                >
                  {isProbing ? <Loader2 className="size-3 animate-spin" /> : <Search className="size-3" />}
                  Verificar red
                </Button>
              </div>

              {isLoadingPrinters && (
                <p className="py-2 text-center text-sm text-muted-foreground">Buscando impresoras...</p>
              )}

              {!isLoadingPrinters && printers.length === 0 && (
                <div className="space-y-1 py-2 text-center">
                  <p className="text-sm text-muted-foreground">No hay impresoras cargadas para este local.</p>
                  <p className="text-xs text-muted-foreground">
                    Cargalas en Impresoras del local o configurá una IP manual abajo.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                {CHANNEL_ROWS.map(({ channel, label, hint, icon: Icon }) => {
                  const target = channelTargets[channel];
                  const isTesting = testingChannel === channel;

                  return (
                    <div
                      key={channel}
                      className="flex flex-col gap-2 rounded-xl border bg-background/60 p-3 sm:flex-row sm:items-center"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2.5">
                        <div className="rounded-full bg-muted p-2 text-muted-foreground">
                          <Icon className="size-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold">{label}</p>
                          <p className="truncate text-xs text-muted-foreground">{hint}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Select
                          value={
                            assignments[channel] && printers.some(printer => printer.id === assignments[channel])
                              ? assignments[channel]
                              : NONE_VALUE
                          }
                          onValueChange={value => setChannelPrinter(channel, value === NONE_VALUE ? null : value)}
                        >
                          <SelectTrigger className="h-9 w-full min-w-0 sm:w-52">
                            <SelectValue placeholder="Sin asignar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE_VALUE}>Sin asignar</SelectItem>
                            {printers.map(printer => (
                              <SelectItem key={printer.id} value={printer.id}>
                                <span className="font-medium">{printer.name}</span>
                                <span className="ml-2 font-mono text-xs text-muted-foreground">
                                  {printer.ip}:{printer.port}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <ReachabilityBadge value={target ? reachability[target.ip] : undefined} />

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-9 shrink-0 gap-1.5 px-3 text-xs"
                          disabled={!target || isTesting}
                          onClick={() => void handleTestPrint(channel)}
                        >
                          {isTesting ? (
                            <Spinner className="size-3.5" />
                          ) : (
                            <Play className="size-3.5 fill-emerald-600/20 text-emerald-600" />
                          )}
                          Probar
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* IP manual */}
              <div className="space-y-2.5 border-t pt-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold tracking-wide text-muted-foreground uppercase">
                    ¿Probar con otra IP / Puerto?
                  </span>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    Actual: {customIp}:{customPort}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customIp}
                    onChange={event => setCustomIp(event.target.value)}
                    placeholder="192.168.68.166"
                    className="h-9 flex-1 rounded-md border bg-background px-3 font-mono text-xs"
                  />
                  <input
                    type="number"
                    value={customPort}
                    onChange={event => setCustomPort(event.target.value)}
                    placeholder="9100"
                    className="h-9 w-24 rounded-md border bg-background px-3 font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-9 shrink-0 px-4 text-xs font-semibold"
                    onClick={() => {
                      const portNum = parseInt(customPort, 10) || 9100;
                      if (!customIp.trim()) {
                        toast.error('Ingresá una dirección IP válida');
                        return;
                      }
                      saveCustomPrinter(customIp.trim(), portNum);
                      toast.success(`Impresora manual configurada y asignada a Orden: ${customIp.trim()}:${portNum}`);
                    }}
                  >
                    Usar
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 border-t pt-2">
            {locationSlug && (
              <Button asChild variant="ghost" size="sm" className="gap-1 text-xs">
                <Link href={`/${locationSlug}/printers`}>
                  Ir a Impresoras del Local
                  <ChevronRight className="size-3.5" />
                </Link>
              </Button>
            )}
            <Button type="button" size="sm" onClick={() => setOpen(false)} className="ml-auto">
              Listo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
